import { lazy, Suspense, useEffect, useRef } from 'react';
import {
  createRouter,
  createRootRoute,
  createRoute,
  Outlet,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { useSessionStore } from '@/entities/session';
import { OrgContextProvider } from '@/entities/organization';
import { AppShell } from '@/widgets/app-shell';
import { BootstrapSplash } from '@/widgets/app-shell';
import { LanguageSwitcher } from '@/widgets/language-switcher';
import { ThemeToggle } from '@/shared/ui/theme-toggle';
import { useTheme, useI18n } from './providers';
import { getEnabledRoutes } from '@/shared/config/modules';
import type { ModuleRouteDescriptor } from '@/shared/config/modules';
import { makeAcceptInviteRoute } from './router/routes.accept-invite';

// ---------------------------------------------------------------------------
// Settings page lazy imports (always-enabled, core settings feature)
// SettingsLayout is the parent shell (left vertical tabs); the three pages are
// rendered into its <Outlet /> as child routes.
// ---------------------------------------------------------------------------
const SettingsLayout = lazy(() => import('@/pages/settings/ui/SettingsLayout'));
const ProfileSettingsPage = lazy(() => import('@/pages/settings/profile/index'));
const SecuritySettingsPage = lazy(() => import('@/pages/settings/security/index'));

// ---------------------------------------------------------------------------
// Notifications page lazy import (always-enabled, core notification feature)
// ---------------------------------------------------------------------------
const NotificationsPage = lazy(() => import('@/pages/notifications'));

// ---------------------------------------------------------------------------
// Invitations accept page lazy import (always-enabled; handles its own auth state)
// The accept link in invitation emails points to /invitations/accept/<token>.
// This page is NOT behind the auth layout guard — it handles the unauthenticated
// case internally by showing a "log in to accept" prompt, which then redirects
// back to this URL after login (via the `redirect` search param).
// ---------------------------------------------------------------------------
const AcceptInvitationPage = lazy(() =>
  import('@/pages/invitations/AcceptInvitationPage').then((m) => ({ default: m.default })),
);

/**
 * Registry-driven TanStack Router tree.
 *
 * Route tree structure:
 *   rootRoute (renders <Outlet /> — no layout)
 *   ├── appLayoutRoute (renders AppShell — authenticated shell)
 *   │   ├── indexRoute    /
 *   │   ├── ...moduleRoutes
 *   │   └── notFoundRoute *
 *   └── authLayoutRoute (auth pages render their own PublicLayout)
 *       ├── /auth/login
 *       ├── /auth/register
 *       ├── /auth/password-reset
 *       ├── /auth/password-reset-confirm
 *       └── /auth/verify-email
 *
 * Guard behaviour:
 *   appLayoutRoute.beforeLoad:
 *     - status === 'unauthenticated' → redirect to /auth/login with ?redirect=<path>
 *     - status === 'idle' | 'bootstrapping' → let through; component renders BootstrapSplash
 *     - status === 'authenticated' → let through; component renders AppShell
 *
 *   authLayoutRoute.beforeLoad:
 *     - status === 'authenticated' → redirect to /
 *     - all other statuses → let through (handles bootstrapping gracefully)
 */

// ---------------------------------------------------------------------------
// Auth page lazy imports (always-enabled, core auth feature)
// ---------------------------------------------------------------------------
const LoginPage = lazy(() => import('@/pages/auth/login'));
// RegisterPage is intentionally not routed — public signup is closed in single-tenant
// mode (Phase 0). The register feature slice (schema + mutation) is retained for Phase 4
// invitation acceptance; the page component is kept but unreachable via the router.
const PasswordResetPage = lazy(() => import('@/pages/auth/password-reset'));
const PasswordResetConfirmPage = lazy(() => import('@/pages/auth/password-reset-confirm'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/verify-email'));

// ---------------------------------------------------------------------------
// Root route — bare outlet, no layout
// ---------------------------------------------------------------------------

/** Root route — no layout wrapper; layout is applied by child layout routes. */
const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// ---------------------------------------------------------------------------
// App layout route — renders AppShell for all protected routes
// ---------------------------------------------------------------------------

/**
 * AppLayoutComponent — the component rendered for all authenticated routes.
 *
 * Reads session status from the Zustand store reactively:
 *  - idle / bootstrapping → BootstrapSplash (no premature redirect)
 *  - unauthenticated      → BootstrapSplash + imperative redirect (once)
 *  - authenticated        → AppShell with nav, user menu, logout
 *
 * The reactive redirect (useEffect) handles the case where the session
 * expires or fails bootstrap AFTER the route has already loaded — e.g.
 * when the user is on a protected page and their token expires.
 *
 * useTheme lives in the app layer and is safe to call here.
 */
function AppLayoutComponent() {
  const status = useSessionStore((s) => s.status);
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  // Guard against re-triggering the redirect (prevents infinite loop when
  // the effect runs again after the navigate call changes router state).
  const redirectedRef = useRef(false);

  // Reactive redirect: when session becomes unauthenticated (e.g. after
  // bootstrap fails or a token expires mid-session), redirect to login.
  // Capture the current href once at effect time (not from location state)
  // to avoid the dependency changing on every navigation.
  useEffect(() => {
    if (status === 'unauthenticated' && !redirectedRef.current) {
      redirectedRef.current = true;
      const currentHref = window.location.pathname + window.location.search;
      void navigate({
        to: '/auth/login',
        search: { redirect: currentHref },
      });
    }
  }, [status, navigate]);

  // While session bootstraps or redirect is in flight, show the splash.
  // This prevents any flash of protected content or the login form.
  if (status === 'idle' || status === 'bootstrapping' || status === 'unauthenticated') {
    return <BootstrapSplash />;
  }

  function handleLogout() {
    // After logout(), the session store status becomes 'unauthenticated'.
    // The reactive useEffect above will trigger navigation to /auth/login.
    redirectedRef.current = false; // allow a fresh redirect
  }

  function handleSettingsClick() {
    void navigate({ to: '/settings/profile' });
  }

  function handleNotificationsClick() {
    void navigate({ to: '/benachrichtigungen' });
  }

  // OrgContextProvider bootstraps the active organization on every authenticated
  // load: it re-fetches the user's orgs, restores the persisted activeOrgId, and
  // repopulates the cached activeOrg object (which is intentionally not persisted).
  // Without it, a full page reload restores the org id but leaves the switcher
  // showing "no organization" because the cached org object is gone.
  return (
    <OrgContextProvider>
      <AppShell
        onLogout={handleLogout}
        onSettingsClick={handleSettingsClick}
        onNotificationsClick={handleNotificationsClick}
        theme={theme}
        onThemeChange={setTheme}
      >
        <Outlet />
      </AppShell>
    </OrgContextProvider>
  );
}

/**
 * App layout route — wraps authenticated app pages in AppShell.
 *
 * beforeLoad guard: redirect unauthenticated users to /auth/login,
 * preserving the intended path in the `redirect` search param.
 * Bootstrap state (idle/bootstrapping) is passed through — the component
 * handles it by rendering the splash screen.
 */
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app-layout',
  component: AppLayoutComponent,
  beforeLoad({ location }) {
    const { status } = useSessionStore.getState();
    // Only redirect when we are certain the user is not authenticated.
    // During bootstrap (idle/bootstrapping) we let the route load; the
    // component renders BootstrapSplash until the status resolves.
    if (status === 'unauthenticated') {
      throw redirect({
        to: '/auth/login',
        search: { redirect: location.href },
      });
    }
  },
});

/**
 * Index route — redirects to the lifts dashboard (/lifts), which is the
 * customer's primary landing page. Uses the same beforeLoad redirect pattern
 * as settingsIndexRoute.
 *
 * `/lifts` is registered through the module registry at runtime, not as a
 * static route in this file, so TanStack Router's compile-time path type does
 * not include it. The `as never` cast is intentional.
 *
 * Integration thread (dev-4): wires the customer portal dashboard.
 */
const indexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  beforeLoad() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    throw redirect({ to: '/lifts' as never });
  },
  component: () => null,
});

/** Not-found route. */
const notFoundRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '*',
  component: () => (
    <div role="alert">
      <h1>404 — Page not found</h1>
    </div>
  ),
});

// ---------------------------------------------------------------------------
// Auth layout route — auth pages render their own PublicLayout
// ---------------------------------------------------------------------------

/**
 * AuthLayoutComponent — chrome shared by all unauthenticated /auth/* pages.
 *
 * Renders a fixed-position control cluster (language switcher + theme toggle)
 * above each page's own PublicLayout card, so users can pick their language
 * and theme during registration/login. useTheme lives in the app layer and is
 * safe to call here; LanguageSwitcher is self-contained (drives shared i18n).
 */
function AuthLayoutComponent() {
  const { theme, setTheme } = useTheme();
  return (
    <>
      <div className="fixed right-4 top-4 z-50 flex items-center gap-2">
        <LanguageSwitcher />
        <ThemeToggle theme={theme} onThemeChange={setTheme} />
      </div>
      <Outlet />
    </>
  );
}

/**
 * Auth layout route — parent of all /auth/* routes.
 *
 * beforeLoad guard: redirect authenticated users to '/'.
 * Bootstrap state (idle/bootstrapping) is passed through to avoid
 * premature redirects while the session is being restored.
 */
const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth-layout',
  component: AuthLayoutComponent,
  beforeLoad() {
    const { status } = useSessionStore.getState();
    // Only redirect away from auth pages once we know the user IS authenticated.
    if (status === 'authenticated') {
      throw redirect({ to: '/' });
    }
  },
});

const authLoginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/auth/login',
  component: () => (
    <Suspense fallback={<div>Loading…</div>}>
      <LoginPage />
    </Suspense>
  ),
});

// /auth/register is intentionally not routed — public signup is closed.
// Phase 4 (invitations) will introduce an invitation-acceptance flow instead.

const authPasswordResetRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/auth/password-reset',
  component: () => (
    <Suspense fallback={<div>Loading…</div>}>
      <PasswordResetPage />
    </Suspense>
  ),
});

const authPasswordResetConfirmRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/auth/password-reset-confirm',
  component: () => (
    <Suspense fallback={<div>Loading…</div>}>
      <PasswordResetConfirmPage />
    </Suspense>
  ),
});

const authVerifyEmailRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/auth/verify-email',
  component: () => (
    <Suspense fallback={<div>Loading…</div>}>
      <VerifyEmailPage />
    </Suspense>
  ),
});

// ---------------------------------------------------------------------------
// Settings routes (always-enabled, core account management)
//
// One centralised Settings view: a layout route renders SettingsLayout (the
// left vertical tab sidebar + <Outlet />), and the three pages are its
// children. The /settings/* URLs are unchanged, so the user-menu link, the MFA
// nav shortcut, and deep links keep working.
// ---------------------------------------------------------------------------

/** /settings — layout shell with the vertical tab sidebar. */
const settingsLayoutRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings',
  component: () => (
    <Suspense fallback={<div>Loading…</div>}>
      <SettingsLayout />
    </Suspense>
  ),
});

/** Bare /settings redirects to the first tab (Profile). */
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/',
  beforeLoad() {
    throw redirect({ to: '/settings/profile' });
  },
  component: () => null,
});

/**
 * /settings/profile — Profile info plus preferences (theme, language,
 * notification opt-in) merged into one view.
 *
 * Injects theme and language callbacks from the app layer (useTheme / useI18n)
 * into the page component so it can apply changes locally on save.
 * This is the correct pattern — the app layer owns these providers.
 */
function ProfileRouteComponent() {
  const { theme, setTheme } = useTheme();
  const { setLanguage } = useI18n();

  return (
    <Suspense fallback={<div>Loading…</div>}>
      <ProfileSettingsPage
        theme={theme}
        onThemeChange={setTheme}
        onLanguageChange={setLanguage}
      />
    </Suspense>
  );
}

const settingsProfileRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: 'profile',
  component: ProfileRouteComponent,
});

/**
 * /settings/security — Change password.
 * No special props needed.
 */
const settingsSecurityRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: 'security',
  component: () => (
    <Suspense fallback={<div>Loading…</div>}>
      <SecuritySettingsPage />
    </Suspense>
  ),
});

/**
 * /settings/preferences — legacy URL; preferences now live on the Profile tab.
 * Redirects so old links and bookmarks keep working.
 */
const settingsPreferencesRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: 'preferences',
  beforeLoad() {
    throw redirect({ to: '/settings/profile' });
  },
  component: () => null,
});

// ---------------------------------------------------------------------------
// Notifications route (always-enabled, core feature)
// ---------------------------------------------------------------------------

/**
 * /benachrichtigungen — Full notifications list page.
 * Protected by the app layout route (requires authentication).
 */
const notificationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/benachrichtigungen',
  component: () => (
    <Suspense fallback={<div>Loading…</div>}>
      <NotificationsPage />
    </Suspense>
  ),
});

/**
 * /notifications — legacy URL; the notifications page now lives at
 * /benachrichtigungen. Redirects so old links and bookmarks keep working.
 * Mirrors the settingsPreferencesRoute beforeLoad-redirect pattern.
 */
const legacyNotificationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/notifications',
  beforeLoad() {
    throw redirect({ to: '/benachrichtigungen' });
  },
  component: () => null,
});

// ---------------------------------------------------------------------------
// Invitations accept route — public route handling its own auth state.
// Matches /invitations/accept/* so the token segment is captured as-is.
// ---------------------------------------------------------------------------

/**
 * Accept invitation route.
 *
 * Lives under rootRoute (not appLayoutRoute) so it is reachable even when
 * the session is unauthenticated. The AcceptInvitationPage component reads
 * the session state and renders a "log in to accept" prompt when needed.
 *
 * Path: /invitations/accept/$token  (the $token suffix makes TanStack Router
 * aware that the last segment is a param, but the page reads it from
 * window.location.pathname directly for simplicity).
 */
const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitations/accept/$token',
  component: () => (
    <Suspense fallback={<div>Loading…</div>}>
      <AcceptInvitationPage />
    </Suspense>
  ),
});

// ---------------------------------------------------------------------------
// Customer accept-invite route (/einladung/:token) — public, no auth guard
// ---------------------------------------------------------------------------

/**
 * Route for the customer invite-acceptance flow.
 *
 * Lives under rootRoute (not appLayoutRoute) so unauthenticated users who
 * received an email invite can reach it. The page is fully self-contained:
 * no session is required; the user sets their password and becomes logged-in
 * as part of the acceptance flow.
 */
const customerAcceptInviteRoute = makeAcceptInviteRoute(rootRoute);

// ---------------------------------------------------------------------------
// Module routes (registry-driven)
// ---------------------------------------------------------------------------

/**
 * Wraps a module route descriptor into a TanStack route under appLayoutRoute.
 */
function routeFromDescriptor(descriptor: ModuleRouteDescriptor) {
  const LazyPage = lazy(descriptor.lazyComponent);

  return createRoute({
    getParentRoute: () => appLayoutRoute,
    path: descriptor.path,
    component: () => (
      <Suspense fallback={<div>Loading…</div>}>
        <LazyPage />
      </Suspense>
    ),
  });
}

const moduleRoutes = getEnabledRoutes().map(routeFromDescriptor);

// ---------------------------------------------------------------------------
// Route tree
// ---------------------------------------------------------------------------

const routeTree = rootRoute.addChildren([
  appLayoutRoute.addChildren([
    indexRoute,
    settingsLayoutRoute.addChildren([
      settingsIndexRoute,
      settingsProfileRoute,
      settingsSecurityRoute,
      settingsPreferencesRoute,
    ]),
    notificationsRoute,
    legacyNotificationsRoute,
    ...moduleRoutes,
    notFoundRoute,
  ]),
  authLayoutRoute.addChildren([
    authLoginRoute,
    // authRegisterRoute removed — public signup closed (Phase 0).
    authPasswordResetRoute,
    authPasswordResetConfirmRoute,
    authVerifyEmailRoute,
  ]),
  // Public route — handles its own auth state (shows login prompt when needed).
  acceptInvitationRoute,
  // Customer accept-invite — public, sets password and logs in as part of flow.
  customerAcceptInviteRoute,
]);

/**
 * The application router.
 *
 * Exported for use in App.tsx (RouterProvider) and in tests.
 */
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

/**
 * TypeScript module augmentation — registers our router type so that
 * TanStack Router's `useNavigate`, `Link`, etc. are fully typed.
 */
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
